import type { Dictionary, Language, Message, TranslationKey, TranslationVars } from './types'

/**
 * The whole resolver. Deliberately hand-rolled rather than react-i18next: this
 * repo already hand-rolled `useWebSocket` over socket.io and rejected Tailwind
 * for the same reason. Two locales, two plural categories, named interpolation —
 * a dependency buys nothing here and costs ~40KB.
 *
 * Reach for a real ICU library only if the copy ever needs gender, ordinals or
 * nested selects.
 */

const pluralRules = new Map<Language, Intl.PluralRules>()

function pluralRuleFor(language: Language): Intl.PluralRules {
  let rule = pluralRules.get(language)
  if (!rule) {
    rule = new Intl.PluralRules(language)
    pluralRules.set(language, rule)
  }
  return rule
}

function selectMessage(message: Message, language: Language, count: number | undefined): string {
  if (typeof message === 'string') return message
  if (count === undefined) return message.other
  return pluralRuleFor(language).select(count) === 'one' ? message.one : message.other
}

/** Replaces every `{name}` with vars.name. An unknown placeholder is left as-is. */
export function interpolate(template: string, vars: TranslationVars | undefined): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name]
    return value === undefined ? match : String(value)
  })
}

export function translate(
  dictionary: Dictionary,
  language: Language,
  key: TranslationKey,
  vars?: TranslationVars,
): string {
  const message = dictionary[key]

  // A missing key renders as the key itself rather than an empty string, so a
  // gap is visible in the UI and in screenshots instead of silently vanishing.
  if (message === undefined) return key

  const count = typeof vars?.['count'] === 'number' ? vars['count'] : undefined
  return interpolate(selectMessage(message, language, count), vars)
}
